import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Star,
  Send,
  Mail,
  Phone,
  MessageSquare,
  Check,
  X,
  Clock,
  Eye,
  Trash2,
  RefreshCw,
  Search,
  Filter,
  Plus,
  Copy,
  ExternalLink,
  ThumbsUp,
  AlertCircle
} from 'lucide-react';

export const Reviews = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('reviews');
  const [reviews, setReviews] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // New Request Modal
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [requestForm, setRequestForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    job_id: '',
    send_via: 'email'
  });
  
  // Review Detail Modal
  const [selectedReview, setSelectedReview] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reviewsRes, requestsRes, jobsRes] = await Promise.all([
        api.get('/reviews/'),
        api.get('/reviews/requests'),
        api.get('/jobs/')
      ]);
      setReviews(reviewsRes.data);
      setRequests(requestsRes.data);
      setJobs(jobsRes.data.filter(j => j.status === 'completed'));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const sendReviewRequest = async (e) => {
    e.preventDefault();
    
    if (!requestForm.customer_name) {
      toast.error('Customer name is required');
      return;
    }
    
    if (requestForm.send_via === 'email' && !requestForm.customer_email) {
      toast.error('Email is required for email delivery');
      return;
    }
    
    if (requestForm.send_via === 'sms' && !requestForm.customer_phone) {
      toast.error('Phone number is required for SMS delivery');
      return;
    }
    
    if (requestForm.send_via === 'both' && (!requestForm.customer_email || !requestForm.customer_phone)) {
      toast.error('Both email and phone are required');
      return;
    }
    
    try {
      const response = await api.post('/reviews/request', requestForm);
      toast.success('Review request sent!');
      setShowRequestModal(false);
      setRequestForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        job_id: '',
        send_via: 'email'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to send review request');
    }
  };

  const resendRequest = async (requestId) => {
    try {
      await api.post(`/reviews/requests/${requestId}/resend`);
      toast.success('Review request resent');
      fetchData();
    } catch (error) {
      toast.error('Failed to resend request');
    }
  };

  const approveReview = async (reviewId, approve = true) => {
    try {
      await api.patch(`/reviews/${reviewId}/approve`, {
        status: approve ? 'approved' : 'rejected',
        display_on_website: approve
      });
      toast.success(approve ? 'Review approved' : 'Review rejected');
      fetchData();
      setSelectedReview(null);
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await api.delete(`/reviews/${reviewId}`);
      toast.success('Review deleted');
      fetchData();
      setSelectedReview(null);
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const copyReviewUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Review link copied to clipboard');
  };

  const filteredReviews = reviews.filter(review => {
    const matchesStatus = statusFilter === 'all' || review.status === statusFilter;
    const matchesSearch = !searchTerm || 
      review.reviewer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: reviews.length,
    pending: reviews.filter(r => r.status === 'pending').length,
    approved: reviews.filter(r => r.status === 'approved').length,
    avgRating: reviews.length > 0 
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reviews-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-[#C6A87C]" />
            Reviews & Testimonials
          </h1>
          <p className="text-gray-500 mt-1">
            Collect and manage customer reviews
          </p>
        </div>
        <button
          onClick={() => setShowRequestModal(true)}
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          data-testid="send-request-btn"
        >
          <Send className="w-4 h-4" />
          Send Review Request
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-100 p-4">
          <p className="text-gray-500 text-xs uppercase tracking-wide">Total Reviews</p>
          <p className="text-3xl font-heading mt-1">{stats.total}</p>
        </div>
        <div className="bg-white border border-gray-100 p-4">
          <p className="text-gray-500 text-xs uppercase tracking-wide">Pending Approval</p>
          <p className="text-3xl font-heading mt-1 text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white border border-gray-100 p-4">
          <p className="text-gray-500 text-xs uppercase tracking-wide">Published</p>
          <p className="text-3xl font-heading mt-1 text-green-600">{stats.approved}</p>
        </div>
        <div className="bg-white border border-gray-100 p-4">
          <p className="text-gray-500 text-xs uppercase tracking-wide">Avg Rating</p>
          <div className="flex items-center gap-2 mt-1">
            <Star className="w-6 h-6 fill-[#C6A87C] text-[#C6A87C]" />
            <span className="text-3xl font-heading">{stats.avgRating}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('reviews')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'reviews'
              ? 'border-[#C6A87C] text-[#C6A87C]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Reviews ({reviews.length})
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'requests'
              ? 'border-[#C6A87C] text-[#C6A87C]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Sent Requests ({requests.length})
        </button>
      </div>

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <div>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search reviews..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Reviews List */}
          {filteredReviews.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-100">
              <MessageSquare className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-gray-400 mb-2">No Reviews Yet</h3>
              <p className="text-gray-400">Send review requests to start collecting feedback</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReviews.map((review) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-gray-100 p-6 hover:border-[#C6A87C]/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedReview(review)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold">{review.reviewer_name}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(review.status)}`}>
                          {review.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= review.rating ? 'fill-[#C6A87C] text-[#C6A87C]' : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="text-sm text-gray-500 ml-2">{review.project_type}</span>
                      </div>
                      {review.title && (
                        <p className="font-medium text-gray-800 mb-1">"{review.title}"</p>
                      )}
                      <p className="text-gray-600 line-clamp-2">{review.content}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(review.created_at).toLocaleDateString()}
                        {review.would_recommend && (
                          <span className="ml-3 text-green-600 flex items-center gap-1 inline-flex">
                            <ThumbsUp className="w-3 h-3" /> Would recommend
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {review.status === 'pending' && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); approveReview(review.id, true); }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                            title="Approve"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); approveReview(review.id, false); }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Reject"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          {requests.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-100">
              <Send className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-gray-400 mb-2">No Requests Sent</h3>
              <p className="text-gray-400">Send your first review request to customers</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white border border-gray-100 p-6"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{request.customer_name}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        {request.customer_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" /> {request.customer_email}
                          </span>
                        )}
                        {request.customer_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" /> {request.customer_phone}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-400 mt-2">
                        <span>Sent: {request.sent_at ? new Date(request.sent_at).toLocaleString() : 'Not sent'}</span>
                        {request.opened_at && (
                          <span className="text-blue-600 flex items-center gap-1">
                            <Eye className="w-3 h-3" /> Opened
                          </span>
                        )}
                        {request.submitted_at && (
                          <span className="text-green-600 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Submitted
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyReviewUrl(request.review_url)}
                        className="p-2 text-gray-400 hover:text-[#C6A87C]"
                        title="Copy link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {!request.submitted_at && (
                        <button
                          onClick={() => resendRequest(request.id)}
                          className="p-2 text-gray-400 hover:text-[#C6A87C]"
                          title="Resend"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Send Request Modal */}
      <AnimatePresence>
        {showRequestModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRequestModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white w-full max-w-lg p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-heading text-xl mb-6">Send Review Request</h3>
              
              <form onSubmit={sendReviewRequest} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Customer Name *</label>
                  <input
                    type="text"
                    value={requestForm.customer_name}
                    onChange={(e) => setRequestForm({ ...requestForm, customer_name: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    placeholder="John Smith"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Email</label>
                  <input
                    type="email"
                    value={requestForm.customer_email}
                    onChange={(e) => setRequestForm({ ...requestForm, customer_email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    placeholder="john@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={requestForm.customer_phone}
                    onChange={(e) => setRequestForm({ ...requestForm, customer_phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Related Job (Optional)</label>
                  <select
                    value={requestForm.job_id}
                    onChange={(e) => setRequestForm({ ...requestForm, job_id: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
                  >
                    <option value="">Select a completed job...</option>
                    {jobs.map((job) => (
                      <option key={job.id} value={job.id}>
                        {job.client_name} - {job.project_type}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Send Via</label>
                  <div className="flex gap-3">
                    {[
                      { value: 'email', label: 'Email', icon: Mail },
                      { value: 'sms', label: 'SMS', icon: Phone },
                      { value: 'both', label: 'Both', icon: Send }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setRequestForm({ ...requestForm, send_via: option.value })}
                        className={`flex-1 p-3 border flex flex-col items-center gap-2 transition-colors ${
                          requestForm.send_via === option.value
                            ? 'border-[#C6A87C] bg-[#C6A87C]/5 text-[#C6A87C]'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <option.icon className="w-5 h-5" />
                        <span className="text-sm">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-200 text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-[#C6A87C] text-white flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Send Request
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Detail Modal */}
      <AnimatePresence>
        {selectedReview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedReview(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading text-xl">{selectedReview.reviewer_name}</h3>
                    <div className="flex items-center gap-1 mt-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${
                            star <= selectedReview.rating ? 'fill-[#C6A87C] text-[#C6A87C]' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded ${getStatusColor(selectedReview.status)}`}>
                    {selectedReview.status}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                {selectedReview.title && (
                  <p className="font-semibold text-lg mb-2">"{selectedReview.title}"</p>
                )}
                <p className="text-gray-700 whitespace-pre-wrap">{selectedReview.content}</p>
                
                <div className="mt-6 pt-6 border-t border-gray-100 space-y-2 text-sm text-gray-500">
                  {selectedReview.project_type && (
                    <p><span className="font-medium text-gray-700">Project:</span> {selectedReview.project_type}</p>
                  )}
                  <p><span className="font-medium text-gray-700">Submitted:</span> {new Date(selectedReview.created_at).toLocaleString()}</p>
                  {selectedReview.would_recommend && (
                    <p className="flex items-center gap-2 text-green-600">
                      <ThumbsUp className="w-4 h-4" /> Would recommend to others
                    </p>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-100">
                  {selectedReview.status === 'pending' && (
                    <>
                      <button
                        onClick={() => approveReview(selectedReview.id, true)}
                        className="flex-1 px-4 py-3 bg-green-600 text-white flex items-center justify-center gap-2"
                      >
                        <Check className="w-4 h-4" />
                        Approve & Publish
                      </button>
                      <button
                        onClick={() => approveReview(selectedReview.id, false)}
                        className="px-4 py-3 border border-red-200 text-red-600 flex items-center justify-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Reject
                      </button>
                    </>
                  )}
                  {selectedReview.status !== 'pending' && (
                    <button
                      onClick={() => deleteReview(selectedReview.id)}
                      className="px-4 py-3 border border-red-200 text-red-600 flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Review
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedReview(null)}
                    className="px-4 py-3 border border-gray-200 text-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Reviews;
